import { Component, inject, signal } from '@angular/core';
import { FirebaseConfigService } from '../../../FirebaseConfig/firebaseConfig.service';
import { AppConfiguratorService } from '../../../layout/components/app.configurator.service';
import { DeviceService } from '../../../services/device.service';

@Component({
  selector: 'app-notification-permission',
  standalone: true,
  template: `
    <div
      class="
    fixed top-4 left-1/2 -translate-x-1/2
    z-[9999]
    w-[90%] max-w-2xl
    bg-surface-0
    border border-surface-200
    rounded-2xl shadow-v1
    px-6 py-4
    flex items-center gap-4
    animate-[slideDown_.25s_ease-out]
  "
    >
      <!-- Ícone -->
      <div
        class="shrink-0 w-10 h-10 rounded-full flex items-center justify-center"
        [class]="state() === 'blocked' ? 'bg-orange-500/12' : 'bg-primary/12'"
      >
        @if (state() === 'blocked') {
          <svg
            class="w-5 h-5 text-orange-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
            />
          </svg>
        } @else {
          <svg
            class="w-5 h-5 text-primary"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
            />
          </svg>
        }
      </div>

      <!-- Texto -->
      <div class="flex-1 min-w-0">
        @if (state() === 'blocked') {
          <p class="text-sm font-semibold text-surface-900">
            Notificações bloqueadas pelo navegador
          </p>
          <p class="text-xs text-surface-500 mt-0.5 leading-relaxed">
            Clique no 🔒 na barra de endereço →
            <strong>Configurações do site</strong> →
            <strong>Notificações: Permitir</strong> → recarregue.
            <span class="text-surface-400 ml-1 select-all"
              >chrome://settings/content/notifications</span
            >
          </p>
        } @else {
          <p class="text-sm font-semibold text-surface-900">
            Ativar notificações
          </p>
          <p class="text-xs text-surface-500 mt-0.5">
            Receba alertas em tempo real sobre eventos das suas câmeras.
          </p>
        }
      </div>

      <!-- Ações -->
      <div class="flex items-center gap-2 shrink-0">
        @if (state() !== 'blocked') {
          <div class="flex flex-col items-end gap-2">
            <!-- Botões -->
            <div class="flex gap-2">
              <button
                (click)="onDeny()"
                class="
                py-2 px-4 rounded-xl
                text-xs font-medium text-surface-600
                border border-surface-200 bg-surface-0
                hover:bg-surface-100
                transition-colors duration-150 cursor-pointer
                whitespace-nowrap
              "
              >
                Agora não
              </button>

              <button
                (click)="onAllow()"
                [disabled]="state() === 'loading'"
                class="
                py-2 px-4 rounded-xl
                text-xs font-medium text-primary-contrast bg-primary
                hover:bg-primary-emphasis
                disabled:opacity-48 disabled:cursor-not-allowed
                transition-colors duration-150 cursor-pointer
                whitespace-nowrap
              "
              >
                {{ state() === 'loading' ? 'Aguarde...' : 'Permitir' }}
              </button>
            </div>

            <!-- Não perguntar novamente — abaixo dos botões, alinhado à direita -->
            <label class="flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                [checked]="neverAskAgain()"
                (change)="neverAskAgain.set(!neverAskAgain())"
                class="w-3.5 h-3.5 rounded accent-primary cursor-pointer"
              />
              <span
                class="text-xs text-surface-400 select-none whitespace-nowrap"
              >
                Não perguntar novamente
              </span>
            </label>
          </div>
        } @else {
          <button
            (click)="onDeny()"
            class="
            py-2 px-4 rounded-xl
            text-xs font-medium text-surface-600
            border border-surface-200 bg-surface-0
            hover:bg-surface-100
            transition-colors duration-150 cursor-pointer
            whitespace-nowrap
          "
          >
            Fechar
          </button>
        }
      </div>
    </div>
  `,
  styles: [
    `
      @keyframes slideDown {
        from {
          transform: translate(-50%, -16px);
          opacity: 0;
        }
        to {
          transform: translate(-50%, 0);
          opacity: 1;
        }
      }
    `,
  ],
})
export class NotificationPermissionComponent {
  private readonly configurator = inject(AppConfiguratorService);
  private readonly deviceService = inject(DeviceService);
  private readonly firebaseService = inject(FirebaseConfigService);

  state = signal<'prompt' | 'blocked' | 'loading'>('prompt');
  neverAskAgain = signal(false);

  async onAllow(): Promise<void> {
    this.state.set('loading');
    const start = Date.now();

    try {
      const permission = await Notification.requestPermission();
      const elapsed = Date.now() - start;

      if (permission === 'denied' && elapsed < 500) {
        this.state.set('blocked');
        return;
      }

      if (permission === 'granted') {
        await this.deviceService.enableNotifications();
        await this.firebaseService.initializeAndGetToken(
          window.location.origin,
          true,
        );
      }

      this.close();
    } catch (err) {
      console.error('Erro ao solicitar permissão:', err);
      this.close();
    }
  }

  onDeny(): void {
    this.close();
  }

  private close(): void {
    this.configurator.dismissNotificationPopup(this.neverAskAgain());
  }
}
